
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRef,
  inject,
  watch,
} from "vue";
import { IBulkDelete } from "@/interfaces/iPageModel";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";
import { FormatMethods } from "@/helpers/data-format";
import { UserAPI } from "@/services/api/User.service";
import { FormApi } from "@/services/api/Form.service";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import {
  userFormTableBulkDelete,
  userFormTableHeaders,
  USER_FORM_LOOKUP,
  USER_SITE_LOOKUP,
} from "../dataTableOptions";
import { IUser, IUserForm } from "@/interfaces/iUser";
import { IForm } from "@/interfaces/iForm";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { getLookups } from "@/services/api/Lookup.service";
import * as Yup from "yup";
import { useToast } from "vue-toastification";
import { ITabConfig } from "@/interfaces/iTabConfig";
import PreviewLink from "@/components/typography/PreviewLink.vue";
import FormDetailModal from "@/views/form/modal/FormDetailModal.vue";
import { hasPermission } from "@/core/services/permission.service";
import { Emitter } from "mitt";
import { debounce } from "lodash";
interface ITabsEmit {
  tabs: ITabConfig[];
  title: string;
  icon?: string;
}
type Events = {
  VZTabsUpdate: ITabsEmit;
};
export default defineComponent({
  name: "UserFrom",
  props: {
    data: { type: Object as PropType<IUser>, required: true },
    openDrawer: { type: Boolean, default: false },
    isActive: { type: Boolean, default: false },
  },
  components: { Datatable, VZDeleteModal, PreviewLink, FormDetailModal },
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const toast = useToast();
    const dataProp = toRef(props, "data");
    const openDrawer = toRef(props, "openDrawer");
    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const defaultFilter = ref({ status: "ACTIVE" });
    const showDetailDialog = ref<any>(false);
    const showDeleteDialog = ref<any>(false);
    const showSearch = true;
    const tableData = ref<IUserForm[]>([]);
    const allTableData = ref<IUserForm[]>([]);
    const selectedForAction = ref<Partial<IUserForm>>({});
    const selectedForm = ref<Partial<IForm>>({});
    const localPreference = ref({});
    const bulkDelete = ref<IBulkDelete>(userFormTableBulkDelete());
    const tableHeader = ref(userFormTableHeaders());
    let siteOptions: { label: any; value: string }[] = [];
    let options: { label: any; value: string }[] = [];
    const formAssignedMsg = t("pages.user.formAssignedMsg");
    const emitter: Emitter<Events> | undefined = inject("emitter");
    const format = computed(() => {
      return FormatMethods;
    });
    const Datatable = ref();
    onMounted(async () => {
      // await refreshList();
      loading.value = true;
    });
    const userSites = async () => {
      siteOptions = [] as any;

      await getLookups(
        `site/userSites/?page=0&size=1000&status=ACTIVE&userId=${dataProp.value.webId}`
      )
        .then((res: any) => {
          siteOptions = res.content.map((data: any) => ({
            label: data.siteName,
            value: data.siteId,
          }));
          formData.value.model[0].options = siteOptions;
        })
        .catch((err) => {
          return [];
        });
    };
    // const observeClickOnClass = () => {
    //   document.body.addEventListener("click", (event) => {
    //     const clickedElement = event.target as HTMLElement;

    //     if (clickedElement.tagName === "SPAN") {
    //       const innerHtmlContent = clickedElement.innerHTML;

    //       const tempElement = document.createElement("div");
    //       tempElement.innerHTML = innerHtmlContent;

    //       // Extract the text content from the parsed HTML
    //       const textContent = tempElement.textContent?.trim();
    //       if (textContent === "Activity Pulse") {
    //         syncQuery();
    //       }
    //     }
    //   });
    // };

    // observeClickOnClass();

    const updatedSitesValue = async (siteId) => {
      loadForm(siteId);
    };
    let permissionModel = ref<any>({
      create: "VYZOR:USERS:USERS:CREATE",
      delete: "VYZOR:USERS:USERS:DELETE",
      update: "VYZOR:USERS:USERS:UPDATE",
    });
    let formData = ref<IAddEditFormModel>({
      header: "Forms",
      page: "Forms",
      edit: false,
      data: null,
      label: [{ addHeader: t("pages.user.assignForm") }],
      validationSchema: Yup.object({
        siteId: Yup.string().required(
          t("pages.site.site") + " " + t("common.isRequired")
        ),
        selectedUserForm: Yup.array()
          .min(1, t("pages.form.form") + " " + t("common.isRequired"))
          .nullable()
          .required(t("pages.form.form") + " " + t("common.isRequired")),
      }),
      model: [
        {
          name: "siteId",
          label: t("pages.site.site"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select branch",
          options: siteOptions,
          dependentFields: ["selectedUserForm"],
          onValueChange: updatedSitesValue,
        },
        {
          name: "selectedUserForm",
          label: t("pages.form.form"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select form",
          options: options,
          multiSelect: true,
        },
      ],
    });

    const loadForm = async (siteId) => {
      options = [] as any;
      loading.value = true;
      await getLookups(`site/filter/SITE_FORMS?siteIds=${siteId}`)
        .then((res) => {
          options = [];
          res.map((data) => {
            options.push({
              label: data.text,
              value: data.value,
            });
          });
          // Filter options array
          const filteredOptions = options.filter((option) => {
            const doesNotMatchFormId = !allTableData.value.some(
              (data) =>
                data.formDefinitionId.toString() == option.value &&
                data.siteId == siteId
            );
            // const doesNotMatchSiteId = !tableData.value.some(
            //   (data) => data.siteId == siteId
            // );
            return doesNotMatchFormId;
          });
          formData.value.model[1].options = filteredOptions;
        })
        .catch((err) => {
          return [];
        })
        .finally(() => {
          loading.value = false;
        });
    };
    const syncQuery = async (params?: Record<string, number | string>) => {
      queryUpdate(dataProp.value.webId, params);
    };

    const queryUpdate = async (
      webId: number,
      params?: Record<string, number | string>
    ) => {
      try {
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
          };
        }
        const data = await UserAPI.getInstance().getUserForms({
          ...params,
          userIds: dataProp.value.webId,
        });
        const allData = await UserAPI.getInstance().getUserForms({
          status: "ACTIVE",
          size: 10000,
          userIds: dataProp.value.webId,
        });
        localPreference.value = { ...params };
        tableData.value = [...data.content];
        allTableData.value = [...allData.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
        await userSites();
      } finally {
        loading.value = false;
      }
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate(dataProp.value.webId, { ...localPreference.value });
    };

    const openDetailModal = async (user) => {
      showDetailDialog.value = true;
      selectedForm.value = {};
      const form = await FormApi.getInstance().getFormById(
        user.formDefinitionId
      );
      selectedForm.value = form;
    };

    const submit = async (reqData) => {
      try {
        const data = reqData.selectedUserForm.map((item) => {
          return {
            formDefinitionId: item,
            userId: dataProp.value.webId,
            siteId: reqData.siteId,
          };
        });
        await UserAPI.getInstance().addUserForms(data);
        toast.success(formAssignedMsg);
        refreshList();
      } catch (e) {
        //
      }
    };

    const webId = computed(() => props.data?.webId);
    let lastFetchedWebId = null;

    const debouncedQueryUpdate = debounce(async (webId) => {
      if (webId !== lastFetchedWebId) {
        tableData.value = [];
        allTableData.value = [];
        lastFetchedWebId = webId;
        defaultFilter.value = { ...defaultFilter.value };
        await queryUpdate(+webId, {
          ...defaultFilter.value,
          page: page.value - 1,
          size: INNER_PAGE_ROWS_PER_PAGE,
        });
      }
    }, 300);

    watch(
      () => props.isActive,
      async (isActive) => {
        if (isActive && dataProp.value.webId) {
          // await queryUpdate(dataProp.value.webId, { ...defaultFilter.value });
          loading.value = true;
          if (Datatable.value?.VZFilter) {
            await Datatable.value.VZFilter.getAppliedFilters();
          }
        } else {
          tableData.value = [];
          allTableData.value = [];
        }
      }
    );
    return {
      Datatable,
      bulkDelete,
      format,
      tableData,
      tableHeader,
      // tableFilter,
      showSearch,
      page,
      total,
      rowsPerPage,
      defaultFilter,
      loading,
      selectedForAction,
      formData,
      syncQuery,
      refreshList,
      submit,
      openDetailModal,
      selectedForm,
      hasPermission,
      permissionModel,
      showDeleteDialog,
      showDetailDialog,
      t,
    };
  },
});
